import { Link } from "@tanstack/react-router";
import { BadgeCheck, ChevronLeft, CircleCheck, Download, Eye, Info, Loader2, Search, Smartphone, Sparkles, WifiOff, X, type LucideIcon } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { accentFor, ApiError, formatBytes, type StoreListing } from "@/api/store.api";
import { cn } from "@/lib/utils";
import { useCatalogView } from "./catalog-page";
import { cardStep } from "./featured-carousel";
import { hardwareIcon, hardwareLabel, permissionIcon, permissionLabel, primaryTag, tagIcon, tagLabel, typeLabel } from "./labels";
import { ListingIcon } from "./listing-icon";
import { useRouteListing } from "./listing-page";

// The phone app's Store screens (mobile/src/app/miniapps/store/{main,detail}.tsx
// and components/store) in its light theme, for the glassly.ai embed. Sizes and
// spacing follow the app; Install is shown as in the app but only opens the listing.
const ICON_RADIUS = 0.28;
const CARD_GAP = 12;
const AUTO_ADVANCE_MS = 3000;

/** Stays false once the visitor touches the store, so returning to the catalog doesn't restart it. */
let autoAdvanceStopped = false;

function AppScreen({ children }: { children: ReactNode }) {
  return (
    <div
      className="min-h-dvh bg-white text-[#0a0a0a] antialiased"
      style={{ fontFamily: 'ui-rounded, "SF Pro Rounded", "Red Hat Display", system-ui, sans-serif' }}
    >
      {children}
    </div>
  );
}

function InstallPill() {
  return (
    <span
      aria-hidden
      className="inline-flex h-[34px] min-w-[88px] shrink-0 items-center justify-center self-center rounded-full bg-[#004dfc] px-4 text-[13px] font-semibold leading-4 text-white"
    >
      Install
    </span>
  );
}

function Verified() {
  return <BadgeCheck className="size-3.5 shrink-0 text-[#86acff]" aria-label="Verified publisher" />;
}

export function AppCatalogPage() {
  const { query, setQuery, tag, setTag, catalog, listings, searching, featured, visible, tags, heading } = useCatalogView();

  return (
    <AppScreen>
      <main className="flex flex-col gap-6 px-6 pt-8 pb-12">
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold">Store</h1>
          <p className="text-sm text-[#737373]">Miniapps for your glasses</p>
        </div>

        {catalog.isError ? (
          <div className="flex items-center gap-3 rounded-2xl bg-[#f5f5f5] px-4 py-3">
            <WifiOff className="size-[18px] shrink-0 text-[#737373]" />
            <span className="flex-1 text-xs">Couldn't reach the store.</span>
            <button type="button" onClick={() => void catalog.refetch()} className="text-xs font-semibold text-[#86acff]">
              Try again
            </button>
          </div>
        ) : null}

        <label className="flex h-12 items-center rounded-2xl bg-[#f5f5f5] px-4">
          <Search className="size-5 shrink-0 text-[#737373]" />
          <input
            type="search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search miniapps"
            autoCapitalize="none"
            autoCorrect="off"
            className="ml-2 min-w-0 flex-1 bg-transparent text-base leading-5 outline-none placeholder:text-[#737373] [&::-webkit-search-cancel-button]:hidden"
          />
          {searching ? (
            <button type="button" aria-label="Clear search" onClick={() => setQuery("")} className="text-[#737373]">
              <X className="size-5" />
            </button>
          ) : null}
        </label>

        {!searching ? (
          <>
            <AppFeaturedCarousel listings={featured} />
            {listings.length > 0 ? (
              <div className="-mx-6 flex gap-2 overflow-x-auto px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {(["all", ...tags] as const).map(item => (
                  <button
                    key={item}
                    type="button"
                    aria-pressed={tag === item}
                    onClick={() => setTag(item)}
                    className={cn(
                      "shrink-0 rounded-full border px-4 py-2 text-[13px] leading-4",
                      tag === item ? "border-[#86acff] bg-[#86acff10] font-semibold text-[#86acff]" : "border-transparent bg-[#f5f5f5]",
                    )}
                  >
                    {tagLabel(item)}
                  </button>
                ))}
              </div>
            ) : null}
          </>
        ) : null}

        <section className="flex flex-col gap-3">
          <h2 className="text-lg font-semibold">{heading}</h2>
          {catalog.isPending ? (
            <div className="flex flex-col items-center gap-3 py-10">
              <Loader2 className="size-5 animate-spin text-[#737373]" />
              <p className="text-sm text-[#737373]">Loading the catalog…</p>
            </div>
          ) : visible.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center">
              {searching ? (
                <p className="text-sm text-[#737373]">No miniapps match "{query.trim()}"</p>
              ) : (
                <>
                  <p className="text-base font-semibold">Nothing published yet</p>
                  <p className="text-sm text-[#737373]">Miniapps appear here as developers publish them.</p>
                </>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {visible.map(listing => (
                <AppListingCard key={listing.packageName} listing={listing} />
              ))}
            </div>
          )}
        </section>
      </main>
    </AppScreen>
  );
}

/** Full-width hero cards, as in the app; advances on its own until the visitor interacts. */
function AppFeaturedCarousel({ listings }: { listings: StoreListing[] }) {
  const scroller = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onScroll = () => {
      const step = cardStep(el, CARD_GAP);
      if (step) setPage(Math.round(el.scrollLeft / step));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [listings.length]);

  useEffect(() => {
    const el = scroller.current;
    if (!el || listings.length < 2 || autoAdvanceStopped) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(() => {
      const step = cardStep(el, CARD_GAP);
      if (!step) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      const next = atEnd ? 0 : Math.round(el.scrollLeft / step) + 1;
      el.scrollTo({ left: next * step, behavior: "smooth" });
    }, AUTO_ADVANCE_MS);
    const events = ["pointerdown", "wheel", "touchstart", "keydown"] as const;
    const clear = () => {
      window.clearInterval(timer);
      for (const event of events) window.removeEventListener(event, stop);
    };
    const stop = () => {
      autoAdvanceStopped = true;
      clear();
    };
    for (const event of events) window.addEventListener(event, stop, { passive: true });
    return clear;
  }, [listings.length]);

  if (listings.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={scroller}
        className="-mx-6 flex snap-x snap-mandatory scroll-px-6 overflow-x-auto px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        style={{ gap: CARD_GAP }}
      >
        {listings.map(listing => (
          <Link
            key={listing.packageName}
            to="/$packageName"
            params={{ packageName: listing.packageName }}
            aria-label={`Featured: ${listing.name}`}
            className="flex aspect-[1/0.62] w-full shrink-0 snap-start flex-col justify-between overflow-hidden rounded-3xl p-5 text-white"
            style={{ backgroundImage: `linear-gradient(135deg, ${accentFor(listing.packageName)} 10%, #0b0f19 90%)` }}
          >
            <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-black/30 px-2.5 py-1 text-[11px] font-semibold leading-[14px]">
              <Sparkles className="size-3" />
              Featured
            </span>
            <div className="flex items-end gap-3">
              <ListingIcon listing={listing} size={56} radius={ICON_RADIUS} />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="truncate text-xl font-bold leading-6">{listing.name}</div>
                <p className="line-clamp-2 text-[13px] leading-[17px] text-white/80">{listing.tagline}</p>
              </div>
              <InstallPill />
            </div>
          </Link>
        ))}
      </div>
      {listings.length > 1 ? (
        <div className="flex justify-center gap-1.5">
          {listings.map((listing, index) => (
            <span
              key={listing.packageName}
              className={cn("h-1.5 rounded-full transition-all", index === page ? "w-4 bg-[#86acff]" : "w-1.5 bg-[#e5e5e5]")}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AppListingCard({ listing }: { listing: StoreListing }) {
  const tag = primaryTag(listing);
  return (
    <Link
      to="/$packageName"
      params={{ packageName: listing.packageName }}
      className="flex items-center gap-3 rounded-2xl bg-[#f5f5f566] px-4 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
    >
      <ListingIcon listing={listing} size={56} radius={ICON_RADIUS} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-base font-semibold">{listing.name}</span>
          {listing.author.verified ? <Verified /> : null}
        </div>
        <p className="line-clamp-2 text-xs text-[#737373]">{listing.tagline}</p>
        {tag ? <p className="mt-0.5 text-[11px] text-[#737373]">{tagLabel(tag)}</p> : null}
      </div>
      <InstallPill />
    </Link>
  );
}

export function AppListingPage() {
  const { packageName, listing } = useRouteListing();
  const title = listing.data?.name ?? "";

  return (
    <AppScreen>
      <header className="relative flex h-14 items-center justify-center px-6">
        <Link to="/" aria-label="Back to the store" className="absolute left-4 flex size-10 items-center justify-center">
          <ChevronLeft className="size-6" />
        </Link>
        <span className="truncate px-12 text-base font-semibold">{title}</span>
      </header>
      <main className="px-6">
        {listing.isPending ? (
          <div className="flex justify-center py-16">
            <Loader2 className="size-5 animate-spin text-[#737373]" />
          </div>
        ) : listing.isError ? (
          <div className="py-16 text-center">
            <p className="text-sm text-[#737373]">
              {listing.error instanceof ApiError && listing.error.status === 404 ? "This miniapp is not in the catalog." : "Couldn't load this miniapp."}
            </p>
            <p className="mt-1 font-mono text-xs text-[#737373]">{packageName}</p>
          </div>
        ) : (
          <AppListingBody listing={listing.data} />
        )}
      </main>
    </AppScreen>
  );
}

function AppListingBody({ listing }: { listing: StoreListing }) {
  const required = listing.permissions.filter(p => p.required !== false);
  const optional = listing.permissions.filter(p => p.required === false);
  const hardware = [...listing.hardwareRequirements].sort((a, b) => (a.level === b.level ? 0 : a.level === "REQUIRED" ? -1 : 1));
  const chips: Array<{ icon: LucideIcon; label: string }> = [
    { icon: listing.type === "standard" ? Smartphone : Eye, label: typeLabel(listing.type) },
    { icon: Download, label: formatBytes(listing.bundleSizeBytes) },
    ...listing.tags.map(tag => ({ icon: tagIcon(tag), label: tagLabel(tag) })),
  ];

  return (
    <div className="flex flex-col gap-6 pt-2 pb-12">
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <ListingIcon listing={listing} size={80} radius={ICON_RADIUS} />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h1 className="line-clamp-2 text-2xl font-bold">{listing.name}</h1>
            <p className="flex items-center gap-1.5 text-sm text-[#737373]">
              {listing.author.name}
              {listing.author.verified ? <Verified /> : null}
            </p>
            {listing.tagline ? <p className="line-clamp-2 text-sm">{listing.tagline}</p> : null}
          </div>
        </div>
        <span aria-hidden className="flex h-[52px] items-center justify-center rounded-2xl bg-[#004dfc] text-base font-semibold text-white">
          Install
        </span>
        <div className="flex flex-wrap gap-2">
          {chips.map(({ icon: Icon, label }) => (
            <span key={label} className="flex items-center gap-1.5 rounded-full bg-[#f5f5f5] px-3 py-1.5 text-xs">
              <Icon className="size-3.5 text-[#737373]" />
              {label}
            </span>
          ))}
        </div>
      </div>

      {listing.screenshots.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-lg font-semibold">On your glasses</h2>
            <p className="text-xs text-[#737373]">What the display shows while this miniapp runs</p>
          </div>
          <div className="-mx-6 flex snap-x snap-mandatory scroll-px-6 gap-3 overflow-x-auto px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {listing.screenshots.map((shot, index) => (
              <img
                key={shot.url}
                src={shot.url}
                alt={`${listing.name} screenshot ${index + 1}`}
                loading="lazy"
                style={{ aspectRatio: shot.width && shot.height ? `${shot.width} / ${shot.height}` : "2 / 1" }}
                className="w-[74%] shrink-0 snap-start rounded-2xl bg-[#f5f5f5] object-cover"
              />
            ))}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold">About</h2>
        <p className="whitespace-pre-line text-sm leading-5">{listing.description || "No description yet."}</p>
      </div>

      <div className="flex flex-col gap-2">
        <Group title="Requested at launch">
          {listing.permissions.length === 0
            ? [<PermissionRow key="none" icon={CircleCheck} label="This miniapp does not ask for any phone permissions." />]
            : [...required, ...optional].map((permission, index) => (
                <PermissionRow
                  key={`${permission.type}-${index}`}
                  icon={permissionIcon(permission.type)}
                  label={permissionLabel(permission.type)}
                  description={permission.description}
                  tag={permission.required === false ? "Optional" : undefined}
                />
              ))}
        </Group>
        {listing.permissions.length > 0 ? (
          <p className="flex items-start gap-1.5 px-1 text-xs text-[#737373]">
            <Info className="size-3.5 shrink-0" />
            Asked when you launch it.
          </p>
        ) : null}
      </div>

      {hardware.length > 0 ? (
        <Group title="Glasses hardware">
          {hardware.map((requirement, index) => (
            <PermissionRow
              key={`${requirement.type}-${index}`}
              icon={hardwareIcon(requirement.type)}
              label={hardwareLabel(requirement.type)}
              description={requirement.description}
              tag={requirement.level.toUpperCase() === "REQUIRED" ? "Required" : "Optional"}
            />
          ))}
        </Group>
      ) : null}

      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-semibold">Details</h2>
        <Group>
          {[
            ["Author", listing.author.verified ? `${listing.author.name} · Verified publisher` : listing.author.name],
            ["Version", listing.version],
            ["Size", formatBytes(listing.bundleSizeBytes)],
            ["Type", typeLabel(listing.type)],
            ...(listing.tags.length > 0 ? [["Tags", listing.tags.map(tagLabel).join(", ")]] : []),
            ["Package", listing.packageName],
          ].map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 bg-[#f5f5f5] px-4 py-5">
              <span className="font-semibold">{label}</span>
              <span className="min-w-0 break-words text-right">{value}</span>
            </div>
          ))}
        </Group>
      </div>
    </div>
  );
}

/** The app's grouped list: 16px outer corners, 4px between rows, 8px gaps. */
function Group({ title, children }: { title?: string; children: ReactNode[] }) {
  return (
    <div className="flex flex-col gap-2">
      {title ? <p>{title}</p> : null}
      {children.map((child, index) => (
        <div
          key={index}
          className={cn(
            "overflow-hidden",
            children.length === 1
              ? "rounded-2xl"
              : index === 0
                ? "rounded-t-2xl rounded-b-[4px]"
                : index === children.length - 1
                  ? "rounded-t-[4px] rounded-b-2xl"
                  : "rounded-[4px]",
          )}
        >
          {child}
        </div>
      ))}
    </div>
  );
}

function PermissionRow({ icon: Icon, label, description, tag }: { icon: LucideIcon; label: string; description?: string; tag?: string }) {
  return (
    <div className="flex items-start gap-3 bg-[#f5f5f566] px-4 py-3 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-white">
        <Icon className="size-[18px] text-[#86acff]" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="flex-1 text-sm font-semibold">{label}</span>
          {tag ? <span className="rounded-full border border-[#737373] px-2 py-0.5 text-[11px] text-[#737373]">{tag}</span> : null}
        </div>
        {description ? <p className="text-xs leading-4 text-[#737373]">{description}</p> : null}
      </div>
    </div>
  );
}
